import type { Sorting } from '@/models/objects/datasetViewState';

/** Add a column with default ASC direction */
export function addSortEntry(list: Sorting[], column: string): Sorting[] {
  return [...list, { column, asc: true }];
}

/** Remove entry at given index */
export function removeSortEntry(list: Sorting[], index: number): Sorting[] {
  return list.filter((_, i) => i !== index);
}

/** Move entry at index up (swap with index-1) */
export function moveSortEntryUp(list: Sorting[], index: number): Sorting[] {
  if (index <= 0 || index >= list.length) return list;
  const result = [...list];
  [result[index - 1], result[index]] = [result[index], result[index - 1]];
  return result;
}

/** Move entry at index down (swap with index+1) */
export function moveSortEntryDown(list: Sorting[], index: number): Sorting[] {
  if (index < 0 || index >= list.length - 1) return list;
  const result = [...list];
  [result[index], result[index + 1]] = [result[index + 1], result[index]];
  return result;
}

/** Toggle direction of entry at index */
export function toggleSortDirection(list: Sorting[], index: number): Sorting[] {
  if (index < 0 || index >= list.length) return list;
  return list.map((entry, i) => (i === index ? { ...entry, asc: !entry.asc } : entry));
}

/** Get columns available to add — uses allDatasetColumns when available, falls back to selectedColumns */
export function getAvailableColumns(
  selectedColumns: string[],
  sortings: Sorting[],
  allDatasetColumns?: string[],
): string[] {
  const source =
    allDatasetColumns && allDatasetColumns.length > 0 ? allDatasetColumns : selectedColumns;
  const sortedColumns = new Set(sortings.map((s) => s.column));
  return source.filter((col) => !sortedColumns.has(col));
}

/** Build an ORDER BY clause from a sort list. Returns empty string if list is empty/undefined. */
export function buildOrderByClause(sortings?: Sorting[]): string {
  if (!sortings || sortings.length === 0) return '';
  return ` ORDER BY ${sortings
    .map((s) => `"${s.column.replace(/"/g, '""')}" ${s.asc ? 'ASC' : 'DESC'}`)
    .join(', ')}`;
}

/** Compute sort-only columns: columns in sortings that are NOT in displayColumns */
export function getSortOnlyColumns(sortings: Sorting[], displayColumns: string[]): string[] {
  const displaySet = new Set(displayColumns);
  return sortings.map((s) => s.column).filter((col) => !displaySet.has(col));
}

/** Remove stale sort entries whose columns no longer exist in the valid column set */
export function removeStaleEntries(sortings: Sorting[], validColumns: string[]): Sorting[] {
  const validSet = new Set(validColumns);
  return sortings.filter((s) => validSet.has(s.column));
}

/** Build SELECT clause with display columns + sort-only columns, all double-quoted */
export function buildSelectClause(displayColumns: string[], sortings?: Sorting[]): string {
  const sortOnlyCols = sortings ? getSortOnlyColumns(sortings, displayColumns) : [];
  const uniqueSortOnly = [...new Set(sortOnlyCols)];
  const allCols = [...displayColumns, ...uniqueSortOnly];
  return allCols.map((c) => `"${c.replace(/"/g, '""')}"`).join(', ');
}
